// src/timers.jsx — Phase 19 rest-timer browser APIs.
// Web Audio + Vibrate + Notification + visibility-resilient timing + visual flash.
// Phase 27 Stage B: migrated to ES modules. Retains window.__llAudioContext
// bridge (singleton AudioContext survives StrictMode double-invoke).
//
// Audio unlock strategy: lazy init on first user gesture (the Start-Workout tap).
// Timing strategy: wall-clock endpoint + 250 ms recomputing tick — survives
// background tab throttling by deriving remaining time from Date.now(), not
// accumulated ticks. visibilitychange forces an immediate tick on return.

// ---- Audio ----

import React from 'react';
import { nativeHaptic } from './native.js';
import { LL } from './tokens.jsx';

function unlockAudioContext() {
  try {
    if (!window.__llAudioContext) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      window.__llAudioContext = new AC();
    }
    const ctx = window.__llAudioContext;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    return ctx;
  } catch (_) {
    return null;
  }
}

function playRestBeep({ sound = true } = {}) {
  if (!sound) return;
  try {
    const ctx = window.__llAudioContext;
    if (!ctx || ctx.state !== 'running') return;

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.25, now + 0.02);
    gain.gain.setValueAtTime(0.25, now + 0.12);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.2);
  } catch (_) {
    // swallow — visual flash + toast cover the gap
  }
}

// ---- Vibration ----

function tryVibrate(ms) {
  // In the Android / iOS app: real haptics (light tap, PR success, rest-over buzz).
  if (nativeHaptic(ms)) return;
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(ms);
    }
  } catch (_) {
    // iOS Safari reports the API as present but no-ops — fine by spec.
  }
}

// ---- Notification ----

function requestNotificationPermission() {
  try {
    if (typeof Notification === 'undefined') return Promise.resolve('unsupported');
    if (Notification.permission !== 'default') return Promise.resolve(Notification.permission);
    // Must run inside a user gesture for Safari + Firefox — caller's responsibility.
    return Notification.requestPermission().catch(() => 'default');
  } catch (_) {
    return Promise.resolve('default');
  }
}

function fireRestNotification(title, body) {
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    if (typeof document !== 'undefined' && !document.hidden) return; // only when tab is hidden
    const opts = {
      body: body || 'Ready for your next set',
      tag: 'liftlog-rest',
      icon: 'icons/icon-192.png',
      renotify: true,
      silent: false,
    };
    // Android Chrome forbids `new Notification()` ("Illegal constructor") and an
    // installed PWA should notify through its service worker anyway, so prefer
    // registration.showNotification and only fall back to the constructor.
    if (typeof navigator !== 'undefined' && navigator.serviceWorker && navigator.serviceWorker.getRegistration) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg && reg.showNotification) return reg.showNotification(title || 'Rest over', opts);
        try { new Notification(title || 'Rest over', opts); } catch (_) {}
      }).catch(() => { try { new Notification(title || 'Rest over', opts); } catch (_) {} });
      return;
    }
    new Notification(title || 'Rest over', opts);
  } catch (_) {
    // Notification constructor throws on some browsers post-deprecation — silent fail.
  }
}

// ---- Timing ----

// startRestTimer(seconds, { onTick, onComplete }) → cancel()
// Drives a React-friendly tick loop that recomputes remaining from wall-clock so
// background throttling does not cause drift. visibilitychange forces a tick.
function startRestTimer(seconds, opts = {}) {
  const { onTick, onComplete } = opts;
  if (!(seconds > 0)) {
    // Zero-duration: fire complete on next microtask and return an inert cancel.
    queueMicrotask(() => { try { onComplete && onComplete(); } catch (_) {} });
    return () => {};
  }

  const startWall = Date.now();
  const endWall = startWall + seconds * 1000;
  let cancelled = false;
  let completed = false;

  const tick = () => {
    if (cancelled || completed) return;
    // Use Math.ceil so a 2-second rest never completes BEFORE 2 seconds elapse.
    // Display decrements as time passes; completion fires exactly at endWall.
    const msLeft = endWall - Date.now();
    const remaining = Math.max(0, Math.ceil(msLeft / 1000));
    try { onTick && onTick(remaining); } catch (_) {}
    if (msLeft <= 0) {
      completed = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVis);
      try { onComplete && onComplete(); } catch (_) {}
    }
  };

  const onVis = () => {
    if (!document.hidden) tick();
  };

  // Fire once immediately so UI shows the initial `seconds` value without a 250 ms delay.
  tick();

  const intervalId = setInterval(tick, 250);
  document.addEventListener('visibilitychange', onVis);

  return function cancel() {
    if (cancelled || completed) return;
    cancelled = true;
    clearInterval(intervalId);
    document.removeEventListener('visibilitychange', onVis);
  };
}

// ---- Visual flash ----

function fireRestFlash() {
  try {
    window.dispatchEvent(new Event('liftlog:restflash'));
  } catch (_) {}
}

// RestFlashOverlay: listens for the `liftlog:restflash` event and pulses an
// acid-green overlay for ~700 ms. Non-interactive. Mounted inside LLPhone.
function RestFlashOverlay() {
  const [active, setActive] = React.useState(false);
  const timerRef = React.useRef(null);

  React.useEffect(() => {
    const onFlash = () => {
      setActive(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setActive(false);
        timerRef.current = null;
      }, 700);
    };
    window.addEventListener('liftlog:restflash', onFlash);
    return () => {
      window.removeEventListener('liftlog:restflash', onFlash);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (!active) return null;
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        // The current accent (acid / amber / cobalt / magenta), not a fixed green.
        background: `color-mix(in oklab, ${LL.accent} 35%, transparent)`,
        zIndex: 55,
        animation: 'restFlashPulse 700ms ease-out',
      }}
    />
  );
}

// Inject the keyframes once so we don't depend on external CSS.
(function injectRestFlashKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('ll-rest-flash-keyframes')) return;
  const style = document.createElement('style');
  style.id = 'll-rest-flash-keyframes';
  style.textContent = `
    @keyframes restFlashPulse {
      0%   { opacity: 0; }
      30%  { opacity: 1; }
      100% { opacity: 0; }
    }
  `;
  document.head.appendChild(style);
})();

export { unlockAudioContext, playRestBeep, tryVibrate, requestNotificationPermission, fireRestNotification, startRestTimer, fireRestFlash, RestFlashOverlay };
